import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, ThumbsUp, MessageSquare, MapPin, Filter, X, ChevronDown, Search, Users, CheckCircle, Loader2 } from 'lucide-react';
import { formatDate } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Issues = () => {
  const navigate = useNavigate();
  const { user, canParticipate, triggerOnboarding } = useAuth();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    state: '',
    district: '',
    constituency: '',
    category: '',
    status: ''
  });
  
  // Geography data (new system)
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState({ districts: false, constituencies: false });
  const [categories, setCategories] = useState([]);
  const statuses = ['Pending', 'In Progress', 'Action Scheduled', 'Resolved', 'Rejected'];

  useEffect(() => {
    fetchStates();
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchIssues();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filters]);

  // Fetch districts when state changes
  useEffect(() => {
    if (filters.state) {
      fetchDistricts(filters.state);
      fetchStateConstituencies(filters.state);
    } else {
      setDistricts([]);
      setConstituencies([]);
    }
  }, [filters.state]);

  // Fetch constituencies when district changes
  useEffect(() => {
    if (filters.district) {
      fetchDistrictConstituencies(filters.district);
    }
  }, [filters.district]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/geo/states`);
      setStates(response.data.states || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      // Fallback to old endpoint
      try {
        const fallback = await axios.get(`${API_URL}/api/states`);
        const stateList = Object.keys(fallback.data).map(name => ({
          state_id: name.toLowerCase().replace(/\s+/g, '_'),
          name: name
        }));
        setStates(stateList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    }
  };

  const fetchDistricts = async (stateName) => {
    setLoadingGeo(prev => ({ ...prev, districts: true }));
    try {
      // Find state_id from state name
      const state = states.find(s => s.name === stateName || s.state_id === stateName);
      if (state) {
        const response = await axios.get(`${API_URL}/api/geo/states/${encodeURIComponent(state.state_id)}/districts`);
        setDistricts(response.data.districts || []);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      // Fallback
      try {
        const response = await axios.get(`${API_URL}/api/geography/districts/${encodeURIComponent(stateName)}`);
        setDistricts((response.data.districts || []).map(d => ({ district_id: d, name: d })));
      } catch (e) {
        setDistricts([]);
      }
    } finally {
      setLoadingGeo(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchStateConstituencies = async (stateName) => {
    setLoadingGeo(prev => ({ ...prev, constituencies: true }));
    try {
      const state = states.find(s => s.name === stateName || s.state_id === stateName);
      if (state) {
        const response = await axios.get(`${API_URL}/api/geo/states/${encodeURIComponent(state.state_id)}/constituencies`);
        setConstituencies(response.data.constituencies || []);
      }
    } catch (error) {
      console.error('Error fetching constituencies:', error);
      setConstituencies([]);
    } finally {
      setLoadingGeo(prev => ({ ...prev, constituencies: false }));
    }
  };

  const fetchDistrictConstituencies = async (districtName) => {
    setLoadingGeo(prev => ({ ...prev, constituencies: true }));
    try {
      const district = districts.find(d => d.name === districtName || d.district_id === districtName);
      if (district) {
        const response = await axios.get(`${API_URL}/api/geo/districts/${encodeURIComponent(district.district_id)}/constituencies`);
        const districtConstituencies = response.data.constituencies || [];
        if (districtConstituencies.length > 0) {
          setConstituencies(districtConstituencies);
        }
      }
    } catch (error) {
      console.error('Error fetching district constituencies:', error);
    } finally {
      setLoadingGeo(prev => ({ ...prev, constituencies: false }));
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/issues/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.state) params.append('state', filters.state);
      if (filters.district) params.append('district', filters.district);
      if (filters.constituency) params.append('constituency', filters.constituency);
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(`${API_URL}/api/issues?${params.toString()}`);
      setIssues(response.data);
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      district: '',
      constituency: '',
      category: '',
      status: ''
    });
    setSearchTerm('');
  };

  const hasActiveFilters = Object.values(filters).some(v => v) || searchTerm;

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved':
        return 'bg-green-500 text-white';
      case 'In Progress':
        return 'bg-blue-500 text-white';
      case 'Action Scheduled':
        return 'bg-purple-500 text-white';
      case 'Rejected':
        return 'bg-red-500 text-white';
      default:
        return 'bg-yellow-500 text-white';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Road': '🛣️',
      'Electricity': '⚡',
      'Water': '💧',
      'Healthcare': '🏥',
      'Education': '📚',
      'Public Safety': '🛡️',
      'Corruption': '⚠️',
      'Government Scheme': '🏛️',
      'Sanitation': '🧹',
      'Environment': '🌳',
      'Transport': '🚌',
      'Housing': '🏠',
    };
    return icons[category] || '📋';
  };

  if (loading && issues.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Issues</h1>
            <p className="text-base text-gray-600">Report and track public issues in your community</p>
          </div>
          <Button
            data-testid="new-issue-btn"
            onClick={() => {
              if (user && !canParticipate()) {
                triggerOnboarding();
              } else {
                navigate('/issues/new');
              }
            }}
            className="bg-blue-600 text-white hover:bg-blue-700 font-medium px-6 py-2.5 rounded-full transition-all shadow-sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Report Issue
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search issues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-issues-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-300' : ''}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {Object.values(filters).filter(v => v).length + (searchTerm ? 1 : 0)}
                </span>
              )}
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700"
                data-testid="clear-filters-btn"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <Select 
                  value={filters.state || 'all'} 
                  onValueChange={(v) => setFilters({...filters, state: v === 'all' ? '' : v, district: '', constituency: ''})}
                >
                  <SelectTrigger data-testid="filter-state">
                    <SelectValue placeholder="All States" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="all">All States</SelectItem>
                    {states.map(state => (
                      <SelectItem key={state.state_id} value={state.name}>{state.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <Select 
                  value={filters.district || 'all'} 
                  onValueChange={(v) => setFilters({...filters, district: v === 'all' ? '' : v, constituency: ''})}
                  disabled={!filters.state || loadingGeo.districts}
                >
                  <SelectTrigger data-testid="filter-district">
                    {loadingGeo.districts ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SelectValue placeholder={filters.state ? "All Districts" : "Select state first"} />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="all">All Districts</SelectItem>
                    {districts.map(district => (
                      <SelectItem key={district.district_id} value={district.name}>{district.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Constituency</label>
                <Select 
                  value={filters.constituency || 'all'} 
                  onValueChange={(v) => setFilters({...filters, constituency: v === 'all' ? '' : v})}
                  disabled={!filters.state || loadingGeo.constituencies}
                >
                  <SelectTrigger data-testid="filter-constituency">
                    {loadingGeo.constituencies ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SelectValue placeholder={filters.state ? "All Constituencies" : "Select state first"} />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="all">All Constituencies</SelectItem>
                    {constituencies.map(constituency => (
                      <SelectItem key={constituency.constituency_id} value={constituency.name}>
                        {constituency.name}
                        {constituency.reserved_category && (
                          <span className="ml-1 text-xs text-gray-500">({constituency.reserved_category})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Select value={filters.category || 'all'} onValueChange={(v) => setFilters({...filters, category: v === 'all' ? '' : v})}>
                  <SelectTrigger data-testid="filter-category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{getCategoryIcon(cat)} {cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {statuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-gray-500">
          Showing {issues.length} issue{issues.length !== 1 ? 's' : ''}
        </div>

        {/* Issues Grid - Responsive: 1/2/3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {issues.map((issue) => (
            <div
              key={issue.issue_id}
              data-testid={`issue-card-${issue.issue_id}`}
              onClick={() => navigate(`/issues/${issue.issue_id}`)}
              className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer group relative"
            >
              {/* Leader Badge - Top Right */}
              {issue.leader_photo && (
                <div 
                  className="absolute top-3 right-3 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/leaders/${issue.primary_leader_id || issue.politician_id}`);
                  }}
                >
                  <div className="relative">
                    <img
                      src={getImageUrl(issue.leader_photo)}
                      alt={issue.leader_name}
                      className="w-10 h-10 rounded-full border-2 border-white shadow-lg object-cover hover:scale-110 transition-transform"
                      title={`${issue.leader_name} (${issue.leader_role})`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {issue.leader_response && (
                      <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5">
                        <CheckCircle className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Image */}
              {issue.image_url ? (
                <div className="relative h-48 sm:h-56 overflow-hidden bg-gray-100 flex items-center justify-center">
                  <img
                    src={getImageUrl(issue.image_url)}
                    alt={issue.title}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                  {/* Status Badge */}
                  <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                    {issue.status}
                  </div>
                  {/* Leader Response Badge */}
                  {issue.leader_response && (
                    <div className="absolute bottom-3 left-3 bg-green-600 text-white px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Leader Responded
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative h-24 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <span className="text-4xl">{getCategoryIcon(issue.category)}</span>
                  <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                    {issue.status}
                  </div>
                  {issue.leader_response && (
                    <div className="absolute bottom-3 left-3 bg-green-600 text-white px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Responded
                    </div>
                  )}
                </div>
              )}

              {/* Content */}
              <div className="p-4">
                {/* Category Badge */}
                {issue.category && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full mb-2">
                    {getCategoryIcon(issue.category)} {issue.category}
                  </span>
                )}

                <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                  {issue.title}
                </h3>

                {/* Location & Time */}
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {issue.constituency}
                  </span>
                  <span>•</span>
                  <span>{formatDate(issue.timestamp)}</span>
                </div>

                {/* Leader Info - if no photo badge shown */}
                {issue.leader_name && !issue.leader_photo && (
                  <div 
                    className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/leaders/${issue.primary_leader_id || issue.politician_id}`);
                    }}
                  >
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-xs text-blue-700 font-medium">
                      Assigned to {issue.leader_name}
                    </span>
                  </div>
                )}

                {/* Reporter */}
                <p className="text-xs text-gray-500 mb-3">
                  By {issue.user_name || 'Anonymous'}
                </p>

                {/* Stats */}
                <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
                  <span className="flex items-center gap-1 text-sm text-gray-600">
                    <ThumbsUp className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">{issue.vote_count}</span>
                    <span className="text-xs text-gray-400">Affected</span>
                  </span>
                  <span className="flex items-center gap-1 text-sm text-gray-600">
                    <MessageSquare className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{issue.comment_count || 0}</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {issues.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No issues found</h3>
            <p className="text-gray-500 mb-6">
              {hasActiveFilters 
                ? 'Try adjusting your filters or search term' 
                : 'Be the first to report an issue in your community'}
            </p>
            <Button
              data-testid="empty-new-issue-btn"
              onClick={() => {
                if (user && !canParticipate()) {
                  triggerOnboarding();
                } else {
                  navigate('/issues/new');
                }
              }}
              className="bg-blue-600 text-white hover:bg-blue-700 font-medium px-6 py-2.5 rounded-full transition-all"
            >
              <Plus className="mr-2 h-4 w-4" />
              Report Issue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Issues;
